import { Alert, Button, Table } from 'antd';
import type { SimulationDetail, SimulationExportMetadata } from '../../store/simulation/types';
import { useSimulationExports } from '../../hooks/simulation/useSimulationExports';
import { triggerBrowserDownload } from '../../hooks/simulation/useSimulationUploadOps';

import type { ColumnsType } from 'antd/es/table';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { createUseStyles } from 'react-jss';
import { format } from 'date-fns/format';

const useStyles = createUseStyles({
  tableStyles: {
    '&& table > .ant-table-thead > tr > th': {
      fontWeight: 'bold',
    },
    '&& table > .ant-table-tbody > tr > td': {
      fontFamily: 'monospace',
      fontSize: '0.9em',
    },
    // disable hover
    '& .ant-table-tbody > tr.ant-table-row:hover > td': {
      background: 'unset',
    },
  },
  buttonLink: {
    fontFamily: 'monospace',
    fontSize: '1em',
    padding: 0,
  },
  button: {
    fontFamily: 'monospace',
    fontSize: '0.9em',
    width: '100px',
  },
});

export interface SimulationExportsTableProps {
  simulation?: SimulationDetail;
}
export const SimulationExportsTable = ({ simulation }: SimulationExportsTableProps) => {
  const classes = useStyles();
  const { exports: simulationExports, loading, isExpired, download } = useSimulationExports(simulation);

  const handleDownload = (file: string) => {
    download(file).then((downloadUrl) => triggerBrowserDownload(downloadUrl, file));
  };

  const columns: ColumnsType<SimulationExportMetadata> = [
    {
      title: 'Name',
      key: 'name',
      align: 'left',
      width: 300,
      render: (val: SimulationExportMetadata) => {
        return val.name;
      },
    },
    {
      title: 'Size',
      key: 'size',
      align: 'right',
      render: (val: SimulationExportMetadata) => {
        return val.sizeKb <= 1000 ? val.sizeKb + ' KB' : (val.sizeKb / 1000).toFixed(1) + ' MB';
      },
    },
    {
      title: 'Last Modified',
      key: 'lastModified',
      align: 'center',
      render: (val: SimulationExportMetadata) => {
        return val.lastModified ? format(new Date(val.lastModified), 'PPpp') : '—';
      },
    },
    {
      title: 'Download',
      key: 'actions',
      align: 'center',
      render: (val: SimulationExportMetadata) => {
        return (
          <Button
            className={classes.button}
            onClick={() => {
              handleDownload(val.name);
            }}
            size={'small'}
          >
            Download
          </Button>
        );
      },
    },
  ];

  return (
    <>
      {isExpired && (
        <Alert
          showIcon={true}
          icon={<ExclamationCircleOutlined />}
          type="warning"
          message="Simulation export documents have expired"
        />
      )}
      {!isExpired && (
        <Table
          loading={loading}
          size={'small'}
          columns={columns}
          dataSource={simulationExports}
          pagination={{ pageSize: 24, showSizeChanger: false }}
          bordered={false}
          className={classes.tableStyles}
        />
      )}
    </>
  );
};
